import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SubscriptionTransferPreview, SubscriptionTransferResult } from "@workspace/api-client-react";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type TransferSource = { id: string; email: string | null };

function displayDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", year: "numeric" }) : "Unchanged";
}

// Mounted afresh for each transfer; the reference is retained on every retry.
export function AdminSubscriptionTransferDialog({ source, onClose }: { source: TransferSource; onClose: () => void }) {
  const [destinationEmail, setDestinationEmail] = useState("");
  const [preview, setPreview] = useState<SubscriptionTransferPreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const { toast } = useToast();

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/admin/users/${source.id}/subscription-transfer/preview`, {
        destinationEmail: destinationEmail.trim().toLowerCase(),
      });
      return await response.json() as SubscriptionTransferPreview;
    },
    onSuccess: (data) => { setPreview(data); setError(""); },
    onError: (err: Error) => { setError(err.message || "Unable to preview the transfer."); },
  });
  const transferMutation = useMutation({
    mutationFn: async () => {
      if (!preview || !confirmed) throw new Error("Review the destination and confirm customer ownership first.");
      setAttempted(true);
      const response = await apiRequest("POST", `/api/admin/users/${source.id}/subscription-transfer`, {
        destinationEmail: preview.destinationEmail,
        expectedSubscriptionId: preview.subscriptionId,
        idempotencyKey,
        ownershipConfirmed: true,
      });
      return await response.json() as SubscriptionTransferResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Subscription transferred", description: data.message });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || "Unable to confirm the result. Retry this same transfer.");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
    },
  });
  const pending = previewMutation.isPending || transferMutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onClose(); }}>
      <DialogContent className="sm:max-w-lg" onInteractOutside={(event) => { if (pending) event.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5" />Transfer subscription</DialogTitle>
          <DialogDescription>
            Move the existing paid subscription to another account owned by this customer. This does not merge accounts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm">
            <span className="text-muted-foreground">From:</span> <span className="font-medium break-all">{source.email}</span>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subscription-transfer-email">Destination account email</Label>
            <Input id="subscription-transfer-email" type="email" value={destinationEmail}
              placeholder="Customer's other account email" autoComplete="off"
              disabled={pending || attempted}
              data-testid="input-transfer-destination"
              onChange={(event) => {
                setDestinationEmail(event.target.value); setPreview(null); setConfirmed(false); setError("");
              }} />
          </div>
          {!preview && (
            <Button variant="outline" disabled={pending || !destinationEmail.trim()}
              data-testid="button-preview-subscription-transfer"
              onClick={() => { setError(""); previewMutation.mutate(); }}>
              {previewMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Check accounts and billing
            </Button>
          )}
          {preview && (
            <div className="space-y-4">
              <dl className="rounded-md border bg-muted/30 p-3 text-sm space-y-2" data-testid="subscription-transfer-preview">
                <div><dt className="text-muted-foreground">Move to</dt><dd className="font-medium break-all">{preview.destinationEmail}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Plan</dt><dd className="capitalize">{preview.tier}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Billing period starts</dt><dd>{displayDate(preview.periodStart)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{preview.cancelAtPeriodEnd ? "Access ends" : "Next renewal"}</dt><dd>{displayDate(preview.renewalDate)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Used allowance carried over</dt><dd>{preview.carriedUsage} quotes</dd></div>
              </dl>
              <p className="text-sm text-muted-foreground">
                No new charge or subscription. Payment method, invoice email, renewal date and any scheduled cancellation stay unchanged.
                Quotes, account data and bonus credits stay with their current accounts. The old account loses paid access;
                the destination&apos;s existing usage also counts towards the current allowance.
              </p>
              <div className="flex items-start gap-2">
                <Checkbox id="subscription-transfer-confirm" checked={confirmed} disabled={pending || attempted}
                  data-testid="checkbox-transfer-ownership"
                  onCheckedChange={(checked) => setConfirmed(checked === true)} />
                <Label htmlFor="subscription-transfer-confirm" className="text-sm leading-5 font-normal cursor-pointer">
                  The customer owns both accounts and has explicitly approved this transfer.
                </Label>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button disabled={!preview || !confirmed || pending} data-testid="button-confirm-subscription-transfer"
            onClick={() => { setError(""); transferMutation.mutate(); }}>
            {transferMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {transferMutation.isPending ? "Transferring..." : attempted ? "Retry same transfer" : "Transfer subscription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}